/**
 * Leader ENABLEMENT aggregation — the Mindtickle education + journey-map peer of `campaignLeader.ts`.
 *
 * Two data sources, joined by the same org tree the rest of the Leader dashboard walks (`orgScope.ts`):
 *   • Education: the per-node `education/completion/by-email/` drill envelope for the focused person —
 *     their `own` detail, a whole-subtree `rollup`, and DIRECT-report summary stubs. The dashboard drills
 *     by refetching a report's email, so these shapers read the shallow node, never a whole tree.
 *   • Attainment: the parallel `goal/attainment/drill/by-email/` envelope (own / rollup / report stubs).
 *   • Journey maps: the bulk `db/journey-map-assignment/` rows, bucketed per user email via the org rows.
 *
 * Education headline figures follow `effectiveEduStats`: the admin-curated REQUIRED set when scope has
 * been curated (`required_total > 0`), else a graceful fallback to the all-series counts so the charts
 * render real data before curation instead of an empty "0 of 0".
 *
 * The per-row figure reads by role, mirroring `campaignEnrollmentLeader.ts`:
 *   • an individual contributor shows their OWN counts (series complete of assigned, maps complete of
 *     assigned);
 *   • a manager (or manager-of-managers) shows "users complete of users" — every IC anywhere in the
 *     subtree, descending THROUGH sub-managers, never counting a sub-manager as a unit.
 *
 * Pure module — no React, no fetching. Callers pass the already-fetched tree, assignment rows and org rows.
 */
import {
  computeSubtreeScope,
  getAllReportsEmails,
  getDirectReports,
  isManagerEmail,
  type OrgChartRow,
} from './orgScope';
import type { ChartPoint } from './campaignLeaderCharts';
import type {
  EducationDrillReport,
  EducationItem,
  EducationSummary,
} from '../hooks/education-completion-hooks';
import type { AttainmentStats } from '../hooks/attainment-hooks';
import {
  assignmentRowDone,
  type JourneyMapAssignmentRow,
} from '../hooks/enablement-journey-map-hooks';

/** The local part of an email, for compact chart-axis labels (`jdoe@cisco.com` → `jdoe`). */
export function shortName(email: string): string {
  const at = email.indexOf('@');
  return at > 0 ? email.slice(0, at) : email;
}

/** Round to two decimal places (percentages, billions) — accurate, never truncated to whole numbers. */
export function round2(n: number | null | undefined): number {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/**
 * Compact USD for the large money figures attainment deals in (quota/bookings run to billions):
 * billions with two decimals (`$1.23B`), stepping down to M / K / plain dollars for smaller values.
 * Returns an em dash for null/unknown so a KPI reads "—" rather than "$0.00".
 */
export function formatUsdCompact(n: number | null | undefined): string {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(2)}K`;
  return `${sign}$${abs.toFixed(2)}`;
}

/** A "done of total" figure for a table cell or KPI. */
export interface EnablementCount {
  done: number;
  total: number;
}

const empty = (): EnablementCount => ({ done: 0, total: 0 });

// ---------------------------------------------------------------------------
// Education (shallow drill node)
// ---------------------------------------------------------------------------

/** A node (or report stub) that exposes an `own` completion summary. */
type OwnSummaryLike = { own?: { summary?: EducationSummary | null } | null } | null | undefined;
/** A node (or report stub) that exposes a subtree `rollup` summary. */
type RollupSummaryLike = { rollup?: { summary?: EducationSummary | null } | null } | null | undefined;

/** A zero education summary, for a missing node or the "unavailable locally" path. */
export const EMPTY_EDU_SUMMARY: EducationSummary = {
  series_total: 0,
  completed_series: 0,
  in_progress_series: 0,
  not_started_series: 0,
  required_total: 0,
  required_completed: 0,
  required_in_progress: 0,
  required_not_started: 0,
  self_enrolled_total: 0,
  self_enrolled_completed: 0,
  informational_total: 0,
  certified_total: 0,
  certified_in_period: 0,
  avg_percent_completed: null,
  required_completion_rate: null,
};

/** A person's own completion summary (or a zero summary when the node is absent). */
export function ownSummary(node: OwnSummaryLike): EducationSummary {
  return node?.own?.summary ?? EMPTY_EDU_SUMMARY;
}

/** A person's whole-subtree rollup summary (or a zero summary when absent). */
export function rollupSummary(node: RollupSummaryLike): EducationSummary {
  return node?.rollup?.summary ?? EMPTY_EDU_SUMMARY;
}

/**
 * The completion figures a headline chart should use. When the required scope has been curated
 * (`required_total > 0`) we report over the REQUIRED set — the intended enablement metric. Until then
 * (`required_total === 0`, the un-curated default) we fall back to ALL assigned series so the charts
 * render real progress instead of an empty "0 of 0". `basis` names which set is in play so callers can
 * surface it in a chart title.
 */
export interface EduStats {
  total: number;
  completed: number;
  inProgress: number;
  notStarted: number;
  /** completed / total as a whole-number percent (0 when nothing assigned). */
  pct: number;
  basis: 'required' | 'all';
}

export function effectiveEduStats(s: EducationSummary): EduStats {
  if (s.required_total > 0) {
    return {
      total: s.required_total,
      completed: s.required_completed,
      inProgress: s.required_in_progress,
      notStarted: s.required_not_started,
      pct: round2((s.required_completed / s.required_total) * 100),
      basis: 'required',
    };
  }
  const total = s.series_total;
  return {
    total,
    completed: s.completed_series,
    inProgress: s.in_progress_series,
    notStarted: s.not_started_series,
    pct: total ? round2((s.completed_series / total) * 100) : 0,
    basis: 'all',
  };
}

/** Effective completion as a whole-number percent (required set, or all-series fallback). */
export function requiredPct(s: EducationSummary): number {
  return effectiveEduStats(s).pct;
}

/** A person's own effective "complete of assigned" (the headline enablement figure). */
export function educationCell(node: OwnSummaryLike): EnablementCount {
  const stats = effectiveEduStats(ownSummary(node));
  return { done: stats.completed, total: stats.total };
}

/** Whether one person finished every effective education series (required set, or all-series fallback). */
export function personEducationCompleted(node: OwnSummaryLike): boolean {
  const stats = effectiveEduStats(ownSummary(node));
  return stats.total > 0 && stats.completed >= stats.total;
}

/** Effective completion % per DIRECT report from a drill node — rollup for a sub-manager, own for an IC. */
export function reportEducationCompletionRows(
  node: { reports?: EducationDrillReport[] } | null | undefined,
): ChartPoint[] {
  return (node?.reports || []).map((r) => {
    const s = r.is_manager ? rollupSummary(r) : ownSummary(r);
    return { label: shortName(r.email), value: effectiveEduStats(s).pct };
  });
}

/** The scope bucket an assigned series falls in (mutually exclusive, mirrors the summary counts). */
export type EduCategory = 'required' | 'self' | 'informational';

/** Bucket one item: required first, then self-enrolled, else informational. */
export function itemCategory(it: EducationItem): EduCategory {
  if (it.required) return 'required';
  if (it.self_enrolled) return 'self';
  return 'informational';
}

export interface CategorizedItems {
  required: EducationItem[];
  self: EducationItem[];
  informational: EducationItem[];
}

/** Split a person's assigned series into the three scope buckets (for the IC detail view). Filters out closed series. */
export function categorizeItems(items?: EducationItem[]): CategorizedItems {
  const out: CategorizedItems = { required: [], self: [], informational: [] };
  for (const it of items || []) {
    // REFINEMENT: skip closed series (closed === true)
    if (it.closed) continue;
    if (it.required) out.required.push(it);
    else if (it.self_enrolled) out.self.push(it);
    else out.informational.push(it);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Journey maps
// ---------------------------------------------------------------------------

/**
 * Bucket every assignment by the person it belongs to, keyed by lowercased email (mapping the `user` id
 * through the org rows, which carry both `user_id` and `user_email`). Assignments we cannot attribute to
 * an email are dropped. Inactive assignments are excluded.
 */
export function assignmentsByUserEmail(
  rows: JourneyMapAssignmentRow[],
  orgRows: OrgChartRow[],
): Map<string, JourneyMapAssignmentRow[]> {
  const emailByUserId = new Map<number, string>();
  for (const r of orgRows) {
    if (r.user_id != null && r.user_email) {
      emailByUserId.set(r.user_id, String(r.user_email).toLowerCase());
    }
  }
  const byEmail = new Map<string, JourneyMapAssignmentRow[]>();
  for (const a of rows) {
    if (a?.is_active === false) continue;
    const email = (emailByUserId.get(a.user) ?? '').trim().toLowerCase();
    if (!email) continue;
    const list = byEmail.get(email) ?? [];
    list.push(a);
    byEmail.set(email, list);
  }
  return byEmail;
}

/** An IC's "journey maps complete of assigned". */
export function journeyCell(
  email: string,
  byEmail: Map<string, JourneyMapAssignmentRow[]>,
): EnablementCount {
  const list = byEmail.get(String(email || '').trim().toLowerCase()) ?? [];
  return { done: list.filter(assignmentRowDone).length, total: list.length };
}

/** Whether one person finished every assigned journey map. */
export function personJourneyCompleted(
  email: string,
  byEmail: Map<string, JourneyMapAssignmentRow[]>,
): boolean {
  const cell = journeyCell(email, byEmail);
  return cell.total > 0 && cell.done >= cell.total;
}

/**
 * A manager's "users complete of users" for JOURNEY MAPS over every IC (non-manager) in the subtree.
 * Sub-managers are descended through, never a unit — mirrors `managerUsersCompleted`.
 */
export function managerJourneyUsers(
  managerEmail: string,
  orgRows: OrgChartRow[],
  byEmail: Map<string, JourneyMapAssignmentRow[]>,
): EnablementCount {
  const icEmails = getAllReportsEmails(orgRows, managerEmail).filter(
    (e) => !isManagerEmail(orgRows, e),
  );
  let done = 0;
  for (const ic of icEmails) {
    if (personJourneyCompleted(ic, byEmail)) done += 1;
  }
  return { done, total: icEmails.length };
}

/**
 * A journey-map roll-up over a set of assignments (MAP grain, not user grain): how many maps are
 * assigned across the group, how many are complete, and the resulting completion rate. This is the
 * "team is X% through their assigned maps" lens, complementing the "users complete of users" lens.
 */
export interface JourneySummary {
  assigned: number;
  completed: number;
  /** assigned − completed (never negative). */
  outstanding: number;
  /** completed / assigned as a whole-number percent (0 when nothing assigned). */
  completionRate: number;
}

/** Sum a flat list of assignments into a map-grain journey figure. */
export function sumJourney(list: JourneyMapAssignmentRow[]): JourneySummary {
  const assigned = list.length;
  const completed = list.filter(assignmentRowDone).length;
  return {
    assigned,
    completed,
    outstanding: Math.max(0, assigned - completed),
    completionRate: assigned ? round2((completed / assigned) * 100) : 0,
  };
}

/**
 * Map-grain journey figure over every person in `email`'s subtree (the person + all descendants,
 * managers included — maps are assigned to individuals at every tier). Deduplicated by assignment id
 * so a person appearing once in the scope is counted once.
 */
export function subtreeJourney(
  email: string,
  orgRows: OrgChartRow[],
  byEmail: Map<string, JourneyMapAssignmentRow[]>,
): JourneySummary {
  const scope = computeSubtreeScope(orgRows, email);
  const seen = new Set<number>();
  const all: JourneyMapAssignmentRow[] = [];
  for (const e of scope.emails) {
    for (const a of byEmail.get(String(e).toLowerCase()) ?? []) {
      if (a?.id != null) {
        if (seen.has(a.id)) continue;
        seen.add(a.id);
      }
      all.push(a);
    }
  }
  return sumJourney(all);
}

/** The role-appropriate journey figure for one report — subtree map-grain for a manager, own for an IC. */
function reportJourneySummary(
  email: string,
  isManager: boolean,
  orgRows: OrgChartRow[],
  byEmail: Map<string, JourneyMapAssignmentRow[]>,
): JourneySummary {
  return isManager
    ? subtreeJourney(email, orgRows, byEmail)
    : sumJourney(byEmail.get(String(email).toLowerCase()) ?? []);
}

// ---------------------------------------------------------------------------
// Combined per-report breakdown (the team members table)
// ---------------------------------------------------------------------------

/** One of a manager's direct reports with role-appropriate education + journey figures. */
export interface ReportEnablement {
  email: string;
  isManager: boolean;
  /** `users complete` for a sub-manager, `series complete` for an IC. */
  education: EnablementCount;
  /** `users complete` for a sub-manager, `maps complete` for an IC. */
  journey: EnablementCount;
  /** Map-grain completion % (subtree for a sub-manager, own for an IC) — a "how far along" companion. */
  journeyRate: number;
}

export function reportIncomplete(cell: EnablementCount): boolean {
  return cell.total > 0 && cell.done < cell.total;
}

/**
 * Each of a manager's DIRECT reports with role-appropriate enablement figures. Education is read from the
 * drill node's report stubs — a sub-manager's subtree `rollup`, an IC's `own` — via `effectiveEduStats`
 * (required set, or all-series fallback). Journey figures stay user-grain from the assignment rows: a
 * sub-manager gets their subtree's "users complete of users", an IC gets their own "maps complete".
 */
export function directReportEnablement(
  managerEmail: string,
  orgRows: OrgChartRow[],
  eduNode: { reports?: EducationDrillReport[] } | null | undefined,
  byEmail: Map<string, JourneyMapAssignmentRow[]>,
): ReportEnablement[] {
  const eduByEmail = new Map<string, EducationDrillReport>();
  for (const r of eduNode?.reports || []) {
    eduByEmail.set(String(r.email || '').toLowerCase(), r);
  }
  return getDirectReports(orgRows, managerEmail).map((r) => {
    const email = String(r.user_email || '').toLowerCase();
    const isManager = !!r.is_manager;
    const journeyRate = reportJourneySummary(email, isManager, orgRows, byEmail).completionRate;
    const eduStub = eduByEmail.get(email);
    const education = eduStub
      ? educationCell(isManager ? { own: eduStub.rollup } : eduStub)
      : empty();
    return {
      email,
      isManager,
      education,
      journey: isManager
        ? managerJourneyUsers(email, orgRows, byEmail)
        : journeyCell(email, byEmail),
      journeyRate,
    };
  });
}

// ---------------------------------------------------------------------------
// Tile shapers — flat `{ label, value }` rows for CampaignChartCard
// ---------------------------------------------------------------------------

/** Series by scope bucket (bar): required / self-enrolled / informational totals. */
export function educationCategoryRows(s: EducationSummary): ChartPoint[] {
  return [
    { label: 'Required', value: s.required_total },
    { label: 'Self-enrolled', value: s.self_enrolled_total },
    { label: 'Informational', value: s.informational_total },
  ];
}

/** Effective-series status mix for one person/subtree (donut): completed / in progress / not started. */
export function educationStatusRows(s: EducationSummary): ChartPoint[] {
  const stats = effectiveEduStats(s);
  return [
    { label: 'Completed', value: stats.completed },
    { label: 'In progress', value: stats.inProgress },
    { label: 'Not started', value: stats.notStarted },
  ];
}

/** Journey-map "users complete vs outstanding" over the IC leaves in a manager's subtree (donut). */
export function journeyUsersDonutRows(
  managerEmail: string,
  orgRows: OrgChartRow[],
  byEmail: Map<string, JourneyMapAssignmentRow[]>,
): ChartPoint[] {
  const cell = managerJourneyUsers(managerEmail, orgRows, byEmail);
  return [
    { label: 'Complete', value: cell.done },
    { label: 'Outstanding', value: Math.max(0, cell.total - cell.done) },
  ];
}

// ---- journey-map tile shapers (the always-available data source) ----------

/**
 * Journey-map completion % per direct report (bar) — the report's subtree map-completion rate for a
 * sub-manager, their own map-completion rate for an IC. The team analogue of `reportEducationCompletionRows`.
 */
export function journeyCompletionByReportRows(
  managerEmail: string,
  orgRows: OrgChartRow[],
  byEmail: Map<string, JourneyMapAssignmentRow[]>,
): ChartPoint[] {
  return getDirectReports(orgRows, managerEmail).map((r) => {
    const email = String(r.user_email || '').toLowerCase();
    return {
      label: shortName(email),
      value: reportJourneySummary(email, !!r.is_manager, orgRows, byEmail).completionRate,
    };
  });
}

/**
 * Completed journey maps per direct report (bar) — absolute volume, so a big team that is only
 * partway through still reads as more completed maps than a small fully-done team. Pairs with the
 * completion-% bar to separate "how much" from "how far along".
 */
export function journeyMapsCompletedByReportRows(
  managerEmail: string,
  orgRows: OrgChartRow[],
  byEmail: Map<string, JourneyMapAssignmentRow[]>,
): ChartPoint[] {
  return getDirectReports(orgRows, managerEmail).map((r) => {
    const email = String(r.user_email || '').toLowerCase();
    return {
      label: shortName(email),
      value: reportJourneySummary(email, !!r.is_manager, orgRows, byEmail).completed,
    };
  });
}

/** Map-grain "completed vs outstanding" over a whole subtree (donut). */
export function journeyMapStatusRows(summary: JourneySummary): ChartPoint[] {
  return [
    { label: 'Completed', value: summary.completed },
    { label: 'Outstanding', value: summary.outstanding },
  ];
}

// ---- per-user journey drill shapers (IC detail) ---------------------------

/** Minimal shape of a resolved journey map for the IC drill (from `useUserJourneyMapDetail`). */
export interface UserJourneyMapLike {
  mapName: string;
  percent: number;
  done: boolean;
}

/** Per-map completion % for one person (horizontal bar) — one bar per assigned map. */
export function userJourneyProgressRows(maps: UserJourneyMapLike[]): ChartPoint[] {
  return (maps || []).map((m) => ({
    label: m.mapName || 'Untitled map',
    value: round2(Number(m.percent) || 0),
  }));
}

/** One person's assigned maps bucketed done / in progress / not started (donut). */
export function userJourneyStatusRows(maps: UserJourneyMapLike[]): ChartPoint[] {
  let completed = 0;
  let inProgress = 0;
  let notStarted = 0;
  for (const m of maps || []) {
    if (m.done) completed += 1;
    else if ((Number(m.percent) || 0) > 0) inProgress += 1;
    else notStarted += 1;
  }
  return [
    { label: 'Completed', value: completed },
    { label: 'In progress', value: inProgress },
    { label: 'Not started', value: notStarted },
  ];
}

// ---------------------------------------------------------------------------
// Attainment (shallow drill node) — parallels the education shapers
// ---------------------------------------------------------------------------

/** Attainment as a percent (0–100) to two decimals, or null when unknown (`attainment_pct` is a 0–1 fraction). */
export function attainmentPct(stats?: Pick<AttainmentStats, 'attainment_pct'> | null): number | null {
  const v = stats?.attainment_pct;
  return v == null ? null : round2(v * 100);
}
